import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import classNames from 'classnames';
import Inputmask from 'inputmask';

import classes from './styles.module.scss';

const MIN_FONT_SIZE = 12;

let measureCanvas;

// Shrinks the font so the whole amount fits between the side paddings
function fitFontSize(input) {
  if (!input) {
    return;
  }

  const { style } = input;
  style.fontSize = '';

  const computed = window.getComputedStyle(input);
  const baseFontSize = parseFloat(computed.fontSize);
  const availableWidth =
    input.clientWidth -
    parseFloat(computed.paddingLeft) -
    parseFloat(computed.paddingRight);

  if (!input.value || availableWidth <= 0) {
    return;
  }

  measureCanvas = measureCanvas || document.createElement('canvas');
  const context = measureCanvas.getContext('2d');
  context.font = `${computed.fontWeight} ${baseFontSize}px ${computed.fontFamily}`;
  // Leave a little room for the caret
  const textWidth = context.measureText(input.value).width + 2;

  if (textWidth > availableWidth) {
    const fittedSize = Math.max(
      MIN_FONT_SIZE,
      Math.floor((baseFontSize * availableWidth) / textWidth)
    );
    style.fontSize = `${fittedSize}px`;
  }
}

export default function CurrencyInput({
  width,
  height,
  readonly,
  name,
  value,
  onChange,
  onBlur,
  onFocus,
  style,
}) {
  const inputRef = useRef(null);

  const fit = useCallback(() => fitFontSize(inputRef.current), []);

  useEffect(() => {
    Inputmask('currency').mask(`.CurrencyInput`);
    fit();
  }, [fit]);

  useLayoutEffect(() => {
    fit();
  }, [value, width, fit]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return undefined;
    }

    // Inputmask reformats the value on its own, so refit on every input event
    input.addEventListener('input', fit);

    let resizeObserver;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(fit);
      resizeObserver.observe(input);
    }

    // Measurements are off until the custom font is loaded
    document.fonts?.ready.then(fit);

    return () => {
      input.removeEventListener('input', fit);
      resizeObserver?.disconnect();
    };
  }, [fit]);

  return (
    <input
      ref={inputRef}
      style={{ ...style, width, height }}
      type="text"
      className={classNames('CurrencyInput', classes.CurrencyInput)}
      readOnly={readonly}
      name={name}
      value={value}
      onChange={onChange}
      onBlur={readonly ? () => {} : onBlur}
      onFocus={onFocus}
    />
  );
}
